// कथा क्रम: put the story's lines back in order. Tap a line (or press Enter / Space) to place it next; tap a placed
// line to send it back; move placed lines with the arrow buttons or Alt + ↑ / ↓. Graded on the server, which alone
// knows where each shuffled line belongs.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { OrderTile, ReaderStory } from '@/prakrit/components/stories/story-logic'

import { useEffect, useRef, useState } from 'react'
import {
    ArrowDown,
    ArrowUp,
    Check,
    Loader2,
    Plus,
    RotateCcw,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useProgress } from '@/prakrit/lib/client/progress'
import { Bi } from '@/prakrit/components/common/bi'
import {
    PRIMARY_BUTTON,
    SECONDARY_BUTTON,
    SectionTitle,
    hiDigits
} from '@/prakrit/components/grammar/labs/lab-ui'
import { postStoryGrade } from '@/prakrit/components/stories/story-logic'
import { XpNote } from '@/prakrit/components/stories/story-questions'

interface OrderResult {
    correct: boolean
    positions: boolean[]
    xp: number
    preview: boolean
}

const TileText: FC<{ tile: OrderTile }> = ({ tile }): ReactNode => (
    <span className='flex min-w-0 flex-1 flex-col gap-0.5 text-left'>
        {tile.speaker && (
            <span className='text-xs text-muted-foreground'>
                {tile.speaker.pra
                    ? <span lang='pra' className='font-prakrit font-semibold text-prakrit'>{tile.speaker.pra}</span>
                    : <span lang='hi' className='hindi font-semibold text-foreground'>{tile.speaker.hi}</span>}
                <span aria-hidden='true'> · </span>
                <span lang='en'>{tile.speaker.en}</span>
            </span>
        )}
        <span lang='pra' className='pra pra-md'>{tile.pra}</span>
    </span>
)

const ARROW = 'flex size-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:border-saffron/60 hover:text-foreground disabled:pointer-events-none disabled:opacity-35 in-data-[mode=kids]:size-11'

interface StoryOrderProps {
    story: ReaderStory
    onSolved: (xp: number) => void
}

export const StoryOrder: FC<StoryOrderProps> = ({ story, onSolved }): ReactNode => {
    const tiles = story.orderTiles
    const count = tiles.length
    const [placed, setPlaced] = useState<number[]>([])
    const [result, setResult] = useState<OrderResult | null>(null)
    const [checking, setChecking] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const focusNext = useRef<string | null>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const { apply } = useProgress()
    const solved = result?.correct === true
    const pool = tiles.map((_, i) => i).filter(i => !placed.includes(i))

    useEffect(() => {
        const selector = focusNext.current

        if (!selector) return

        focusNext.current = null
        rootRef.current?.querySelector<HTMLElement>(selector)?.focus()
    }, [placed, result])

    const change = (next: number[], focus: string) => {
        if (solved || checking) return

        focusNext.current = focus
        setPlaced(next)
        setResult(null)
        setError(null)
    }

    const place = (tile: number) => {
        const at = pool.indexOf(tile)
        const remaining = pool.filter(i => i !== tile)
        const target = remaining[at] ?? remaining[at - 1]

        change([...placed, tile], target === undefined ? '[data-check]' : `[data-pool="${target}"]`)
    }

    const remove = (position: number) => {
        const tile = placed[position]

        change(placed.filter((_, i) => i !== position), `[data-pool="${tile}"]`)
    }

    const move = (position: number, delta: -1 | 1, fromArrow: boolean) => {
        const to = position + delta

        if (to < 0 || to >= placed.length) return

        const next = [...placed]
        const tile = next[position]

        next[position] = next[to]
        next[to] = tile

        const edge = delta < 0 ? to === 0 : to === placed.length - 1
        const focus = fromArrow && !edge ? `[data-move="${tile}-${delta < 0 ? 'up' : 'down'}"]` : `[data-placed="${tile}"]`

        change(next, focus)
    }

    const onPlacedKey = (event: KeyboardEvent<HTMLButtonElement>, position: number) => {
        if (event.altKey && event.key === 'ArrowUp') {
            event.preventDefault()
            move(position, -1, false)
        }
        else if (event.altKey && event.key === 'ArrowDown') {
            event.preventDefault()
            move(position, 1, false)
        }
        else if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault()
            remove(position)
        }
    }

    const reset = () => change([], '[data-pool]')

    const check = async () => {
        if (placed.length !== count || checking || solved) return

        setChecking(true)
        setError(null)

        try {
            const response = await postStoryGrade(story.id, { kind: 'order', order: placed })
            const positions = response.positions ?? placed.map(() => false)

            if (!response.preview) apply({ receipts: response.receipt ? [response.receipt] : [], attempts: response.attempt ? [response.attempt] : [] })

            setResult({ correct: response.correct, positions, xp: response.xpAwarded, preview: response.preview })

            if (response.correct) onSolved(response.xpAwarded)
        }
        catch (failure) {
            setError(failure instanceof Error ? failure.message : 'The order could not be checked.')
        }
        finally {
            setChecking(false)
        }
    }

    if (count < 2) return null

    const inPlace = result ? result.positions.filter(Boolean).length : 0

    return (
        <section aria-labelledby='story-order' className='flex flex-col gap-5'>
            <SectionTitle id='story-order' hi='क्रम से लगाइए' en='Put the lines in order'>
                <Bi
                    hi='पंक्तियाँ उलट-पुलट हो गई हैं। नीचे से एक-एक पंक्ति चुनकर कथा का सही क्रम बनाइए, फिर जाँचिए।'
                    en='The lines are mixed up. Pick them one by one from below to rebuild the story, then check.'
                    className='text-[0.9375rem]'
                />
                <p className='adults-only text-xs text-muted-foreground'>
                    <span lang='hi' data-ex='hi' className='hindi'>कीबोर्ड: Enter से लगाएँ, Alt + ↑ / ↓ से खिसकाएँ, Delete से हटाएँ।</span>
                    <span aria-hidden='true' className='ex-sep'> · </span>
                    <span lang='en' data-ex='en'>Keyboard: Enter places a line, Alt + ↑ / ↓ moves it, Delete takes it out.</span>
                </p>
            </SectionTitle>

            <div ref={rootRef} className='flex flex-col gap-5 rounded-xl border border-border bg-card p-5 shadow-card in-data-[mode=kids]:border-2'>
                <div className='flex flex-col gap-2'>
                    <p id={`${story.id}-order-label`} className='text-sm font-semibold'>
                        <Bi hi='आपका क्रम' en='Your order' layout='inline' enClassName='font-medium text-muted-foreground' />
                    </p>
                    <ol aria-labelledby={`${story.id}-order-label`} className='flex flex-col gap-2'>
                        {Array.from({ length: count }, (_, position) => {
                            const tile = placed[position]
                            const number = (
                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'hindi flex size-9 shrink-0 items-center justify-center self-center rounded-full border text-sm font-semibold in-data-[mode=kids]:size-11',
                                        result && tile !== undefined
                                            ? result.positions[position] ? 'border-success bg-success text-white dark:text-background' : 'border-danger bg-danger text-white dark:text-background'
                                            : 'border-border bg-background text-muted-foreground'
                                    )}
                                >
                                    {result && tile !== undefined
                                        ? result.positions[position] ? <Check className='size-4' /> : <X className='size-4' />
                                        : hiDigits(position + 1)}
                                </span>
                            )

                            if (tile === undefined) {
                                return (
                                    <li key={`empty-${position}`} className='flex items-center gap-3'>
                                        {number}
                                        <span aria-hidden='true' className='h-12 flex-1 rounded-xl border-2 border-dashed border-border bg-surface-muted/40' />
                                        <span className='sr-only'>{position + 1}: खाली · empty</span>
                                    </li>
                                )
                            }

                            const mark = result ? (result.positions[position] ? 'सही जगह · in place' : 'गलत जगह · not in place') : null

                            return (
                                <li key={`tile-${tile}`} className='flex items-stretch gap-3'>
                                    {number}
                                    <button
                                        type='button'
                                        data-placed={tile}
                                        disabled={solved}
                                        onClick={() => remove(position)}
                                        onKeyDown={event => onPlacedKey(event, position)}
                                        className={cn(
                                            'flex min-h-tap min-w-0 flex-1 items-center gap-3 rounded-xl border-2 px-4 py-2 shadow-sm transition-colors',
                                            result
                                                ? result.positions[position] ? 'border-success bg-success-soft' : 'border-danger/60 bg-danger-soft'
                                                : 'border-saffron/50 bg-saffron/5 hover:border-saffron',
                                            'disabled:cursor-default'
                                        )}
                                    >
                                        <TileText tile={tiles[tile]} />
                                        <span className='sr-only'>
                                            {`, ${position + 1}`}
                                            {mark ? `, ${mark}` : ''}
                                            {solved ? '' : ' — हटाने के लिए दबाएँ · press to take it out'}
                                        </span>
                                    </button>
                                    <span className='flex shrink-0 flex-col justify-center gap-1'>
                                        <button
                                            type='button'
                                            data-move={`${tile}-up`}
                                            disabled={solved || position === 0}
                                            onClick={() => move(position, -1, true)}
                                            className={ARROW}
                                        >
                                            <ArrowUp className='size-4' aria-hidden='true' />
                                            <span className='sr-only'>ऊपर ले जाएँ · Move up, line {position + 1}</span>
                                        </button>
                                        <button
                                            type='button'
                                            data-move={`${tile}-down`}
                                            disabled={solved || position === placed.length - 1}
                                            onClick={() => move(position, 1, true)}
                                            className={ARROW}
                                        >
                                            <ArrowDown className='size-4' aria-hidden='true' />
                                            <span className='sr-only'>नीचे ले जाएँ · Move down, line {position + 1}</span>
                                        </button>
                                    </span>
                                </li>
                            )
                        })}
                    </ol>
                </div>

                <div className='leaf-rule' aria-hidden='true' />

                <div className='flex flex-col gap-2'>
                    <p id={`${story.id}-pool-label`} className='text-sm font-semibold'>
                        <Bi hi='बची हुई पंक्तियाँ' en='Lines to place' layout='inline' enClassName='font-medium text-muted-foreground' />
                    </p>
                    {pool.length === 0 ? (
                        <p className='rounded-xl bg-surface-muted/60 px-4 py-3 text-sm text-muted-foreground'>
                            <Bi hi='सब पंक्तियाँ लग गईं।' en='Every line is placed.' layout='inline' />
                        </p>
                    ) : (
                        <div role='group' aria-labelledby={`${story.id}-pool-label`} className='flex flex-col gap-2'>
                            {pool.map(tile => (
                                <button
                                    key={tile}
                                    type='button'
                                    data-pool={tile}
                                    disabled={solved || checking}
                                    onClick={() => place(tile)}
                                    className='flex min-h-tap w-full items-center gap-3 rounded-xl border-2 border-border bg-card px-4 py-2 shadow-sm transition-colors hover:border-saffron/60 hover:bg-saffron/5'
                                >
                                    <Plus className='size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                                    <TileText tile={tiles[tile]} />
                                    <span className='sr-only'> — अगली जगह पर लगाएँ · place next</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className='flex flex-wrap items-center gap-3'>
                    <button
                        type='button'
                        data-check
                        onClick={check}
                        disabled={placed.length !== count || checking || solved}
                        className={PRIMARY_BUTTON}
                    >
                        {checking ? <Loader2 className='size-4 animate-spin' aria-hidden='true' /> : <Check className='size-4' aria-hidden='true' />}
                        <span lang='hi' className='hindi'>जाँचें</span>
                        <span lang='en' className='font-medium'>Check</span>
                    </button>
                    <button
                        type='button'
                        onClick={reset}
                        disabled={placed.length === 0 || checking || solved}
                        className={SECONDARY_BUTTON}
                    >
                        <RotateCcw className='size-4' aria-hidden='true' />
                        <span lang='hi' className='hindi'>फिर से</span>
                        <span lang='en' className='text-muted-foreground'>Start over</span>
                    </button>
                </div>

                <div aria-live='polite' className='min-h-6'>
                    {result?.correct && (
                        <p className='flex flex-wrap items-center gap-2 text-sm font-medium text-success'>
                            <Check className='size-4' aria-hidden='true' />
                            <Bi hi='शाबाश! पूरा क्रम सही है।' en='Well done! The whole order is right.' layout='inline' enClassName='text-success/80' />
                            <XpNote xp={result.xp} preview={result.preview} />
                        </p>
                    )}
                    {result && !result.correct && (
                        <p className='flex items-start gap-2 text-sm'>
                            <X className='mt-0.5 size-4 shrink-0 text-danger' aria-hidden='true' />
                            <Bi
                                hi={`${hiDigits(count)} में से ${hiDigits(inPlace)} पंक्तियाँ सही जगह पर हैं। लाल निशान वाली पंक्तियों को खिसकाइए और फिर जाँचिए।`}
                                en={`${inPlace} of ${count} lines are in place. Move the ones marked in red and check again.`}
                            />
                        </p>
                    )}
                    {error && (
                        <p className='flex items-start gap-2 text-sm text-danger'>
                            <X className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                            <span className='flex flex-col'>
                                <span lang='hi' className='hindi'>क्रम की जाँच नहीं हो सकी। कृपया फिर से जाँचें।</span>
                                <span lang='en'>{error}</span>
                            </span>
                        </p>
                    )}
                </div>
            </div>
        </section>
    )
}
